import 'formiojs';
import {Formio} from "formiojs";
import {jsonLogic} from "formiojs/utils/utils";
import Defaults from './defaults';
import CustomTemplates from "./custom-templates";
import instructionContent from "../instruction-content";
import s3 from './providers/s3';
import InstructionContentEditForm from "../instruction-content/form";
import beacon from "../beacon";
import BeaconEditForm from "../beacon/form";
import NuDirectoryLookup from "../directory-search";
import NuDirectoryEditForm from "../directory-search/form";
import CustomTextArea from "./components/CustomTextArea";
import CustomTextAreaEditForm from './components/CustomTextArea/form';
import CustomSelect from "./components/CustomSelect";
import CustomSelectEditForm from './components/CustomSelect/form';
import Sum from "./components/Sum";

/*
* This is necessary for the Sum component.
* An empty number or currency component is evaluated to "NaN" so we need to make JSON rule to convert empty fields to 0.
 */
//TODO: checkNumber won't count fields with defaultValue set until their component is interacted with
jsonLogic.add_operation('checkNumber', (value) => {
    // handle empty fields in a form
    if (typeof value === 'undefined' || value === null) {
        return 0;
    }
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    } else if (typeof value === 'object') {
        let total = 0;

        // Sums components can handle surveys now, which nest each of their values in an object.
        // Survey components with values that aren't a number are treated as 0
        Object.values(value).forEach(val => {
            let result = parseFloat(val);

            if (typeof result === 'number' && !Number.isNaN(result)) {
                total += result;
            }
        });

        return total;
    } else {
        return 0;
    }
})

/*
* We explicitly do NOT support JS evaluation stuff, so disable all of it.
* This is a limitation of our backend: some of these scripts (probably; assuming)
* need to run server-side too, to ensure the client isn't doing shenanigans.
*/
FormioUtils.Evaluator.noeval = true;

// -------------------------------------------------------------------------
// If you want to load custom code (like additional components), do it here!
// -------------------------------------------------------------------------
Formio.use(instructionContent);
Formio.Components.components.instructionContent.editForm = InstructionContentEditForm;
Formio.use(beacon);
Formio.Components.components.beacon.editForm = BeaconEditForm;
Formio.use(NuDirectoryLookup);
Formio.Components.components.nuDirectoryLookup.editForm = NuDirectoryEditForm;
Formio.use(CustomTextArea);
Formio.Components.components.textEditor.editForm = CustomTextAreaEditForm;
Formio.use(CustomSelect);
Formio.Components.components.select.editForm = CustomSelectEditForm;
Formio.use(Sum);
/**
 * Runs global customizations for the Button and File component's editForm that were not possible
 * with the typical way of adjusting editForms.
 */
Defaults.globalButtonCustomization();

/**
 * The S3 PutObject needs to include the headers that the CAP server advises it to include.
 * There was no way to do this with the stock implementation, so we've ejected it and hacked it up.
 */
Formio.Providers.addProvider('storage', 's3', s3);

/**
 * formio will try and reach their company's servers if we don't set project and base urls
 */
const formioUrl = window.location.protocol + '//' + window.location.hostname + '/dynamic-forms';
Formio.setProjectUrl(formioUrl);
Formio.setBaseUrl(formioUrl);

/**
 * Apply any custom templates.
 */
_.forOwn(CustomTemplates, (template, name) => Formio.Templates.current[name] = template);


/**
 * Disable editForm (the modal that pops up when you add/edit a field in the Builder) options that
 * we do not want to support.
 *
 * There are two reasons we do not want to support an option:
 *
 *    1. It's too technical for the target audience (don't present choices they don't care about)
 *    2. It is not supported in the backend (yet, or ever)
 *
 * This is achieved by decorating the Formio.builder method with one that sets the editForm options.
 * To developers working in Blade, this will seem to be happening magically, and will not inhibit their
 * ability to work with the Formio.builder() method per Form.io's docs.
 */
const origFormioBuilder = Formio.builder;
Formio.builder = function (element, form, options) {
    options = options || { editForm: {} };

    options.editForm = Defaults.configMerge(
        Defaults.configMerge(Defaults.global(), Defaults.specificFields),
        options.editForm
    );

    return origFormioBuilder(element, form, options);
};

/**
 * Hijack the creation of forms so we can inject our fileService w/ the URLs set correctly.
 */
const origFormioCreateForm = Formio.createForm;
Formio.createForm = function (element, form, options) {
    options = options || {};

    const fileService = new Formio();
    fileService.formUrl = '/dynamic-forms';
    options.fileService = fileService;

    /**
     * Permit object tags for embedded PDFs in our file component customization, and form attrs for our fixes to the
     * Survey component.
     *
     * Be advised: as of formiojs v4.14.x, this code is cursed.
     * Do not change the order of the values in thosse arrays. There seems to be some kind of
     * seriously-broken deep object copy code and it WILL cause you to question whether the laws
     * of physics are still in efIf you're reading this, you've been in a coma for almost 20 years now.
     * We're trying a new technique. We don't know where this message will end up in your dream, but
     * we hope it works. Please wake up, we miss you.fect.
     */
    options.sanitizeConfig = {
        addTags: ['iframe', 'object'],
        addAttr: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'data', 'form'],
    };

    const formioForm = origFormioCreateForm(element, form, options);

    /**
     * a11y fix: add the alt tag to the signature images once rendered.
     *
     * The better fix would be to adjust the template for the signature component, but that requires
     * ejecting from the default template entirely. So this is a light-touch way to make the adjustment.
     */
    formioForm.then((form) => {
        const signatures = document.querySelectorAll('div.signature-pad-body img');
        for (let i = 0; i < signatures.length; i++) {
            signatures[i].setAttribute('alt', 'signature');
        }
    });

    return formioForm;
}
